import { type PulseError, removeEmptyParams } from '@prenuvo/pulse';
import { useQuery, UseQueryOptions } from '@tanstack/react-query';

import { ENDPOINTS } from '@/api/endpoints';
import { httpClient } from '@/api/http-client';
import {
  RadiologistScanDetail,
  RadiologistScanDetailRequest,
  StudiesRequest,
  StudiesResponse,
} from '@/api/model';

const KEY = {
  GET_RADIOLOGIST_SCAN_DETAILS: 'GET_RADIOLOGIST_SCAN_DETAILS',
  GET_STUDIES: 'GET_STUDIES',
} as const;

const request = {
  getRadiologistScanDetails: async (
    {
      payload: {
        params: { study_uuid: studyUuid },
      },
    }: RadiologistScanDetailRequest,
    signal?: AbortSignal,
  ) => {
    const { data } = await httpClient.get<RadiologistScanDetail>(
      `${ENDPOINTS.APPS.STUDY}/${studyUuid}/radiologist-scan-details`,
      { signal },
    );

    return data;
  },
  getStudies: async ({ payload: { params } }: StudiesRequest, signal?: AbortSignal) => {
    const { data } = await httpClient.get<StudiesResponse>(ENDPOINTS.APPS.STUDIES, {
      params: removeEmptyParams(params),
      signal,
    });

    return data;
  },
};

const query = {
  useGetRadiologistScanDetails: (
    { payload }: RadiologistScanDetailRequest,
    config?: Partial<UseQueryOptions<RadiologistScanDetail, PulseError>>,
  ) =>
    useQuery<RadiologistScanDetail, PulseError>({
      enabled: !!payload.params.study_uuid,
      queryFn: ({ signal }) => request.getRadiologistScanDetails({ payload }, signal),
      queryKey: [KEY.GET_RADIOLOGIST_SCAN_DETAILS, payload.params.study_uuid],
      ...config,
    }),
  useGetStudies: (
    { payload }: StudiesRequest,
    config?: Partial<UseQueryOptions<StudiesResponse, PulseError>>,
  ) =>
    useQuery<StudiesResponse, PulseError>({
      enabled: !!payload.params.user_id,
      queryFn: ({ signal }) => request.getStudies({ payload }, signal),
      queryKey: [KEY.GET_STUDIES, payload.params.user_id],
      ...config,
    }),
};

export const study = {
  KEY,
  query,
  request,
};
